import { Trans } from 'react-i18next'
import clsx from 'clsx'
import { t } from 'i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter } from '@makyoapp/makyo-common'
import { RandomAlt16, Table16 } from '@makyoapp/makyo-icon/icon'
import { SelectDropdown } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useUpdateCompSettingValue } from '@/hooks/useUpdateCompSettings'
import { BuilderComponentSetting } from '@/shared/builder'
import { COLUMN_SELECT_ROW_ID_KEY, TEXT_ABLE_COLUMN_TYPES_TEXT_ONLY } from '@/shared/builder-constants'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { RenderColumn } from '@/components/RenderColumn'

interface SortBySelectFieldSettingProps {
  id: string
  settingId: string
  value: any
  sortBySelectSettings: BuilderComponentSetting['sortBySelectSettings']
}

const SortBySelectFieldSetting = ({ id, settingId, value, sortBySelectSettings }: SortBySelectFieldSettingProps) => {
  const { tables } = useDbClientContext()
  const { activeComponent, activeLayerContainer } = useActiveLayerContext()
  const updateValue = useUpdateCompSettingValue(id)

  const tableSourceId = sortBySelectSettings?.tableSourceId
  const columnTypes = sortBySelectSettings?.columnTypes

  const tableSourceValue: undefined | string | { table: string } =
    activeComponent && tableSourceId
      ? (activeLayerContainer?.elementSettings[activeComponent.id]?.[tableSourceId] as string | { table: string })
      : undefined

  const tbl = typeof tableSourceValue === 'string' ? tableSourceValue : tableSourceValue?.table

  const table = tables?.find((tb) => tb.id === tbl)
  const columns = [
    ...(table?.columns || []),
    {
      id: COLUMN_SELECT_ROW_ID_KEY,
      type: ColumnTypes.RowID,
      name: t('Row ID'),
    },
  ].filter(({ type }) => TEXT_ABLE_COLUMN_TYPES_TEXT_ONLY.includes(type))

  const filteredColumns = columnTypes
    ? columns.filter(({ type }) => {
        return columnTypes.includes(type)
      })
    : columns

  const finalOptions = [
    {
      id: '',
      type: 'table',
    },
    {
      id: '#random#',
      type: 'random',
    },
    '#div#',
    `#{${t('Sort By Column')}}#`,
    ...filteredColumns,
  ] as ColumnAdapter[]

  return (
    <SelectDropdown
      withSearch={filteredColumns.length > SHOW_SEARCH_LIMIT}
      id={settingId}
      value={finalOptions.find(({ id }) => id === value)}
      onChange={async (opt) => {
        const val = (opt as ColumnAdapter)?.id
        await updateValue(val === '' ? undefined : val)
      }}
      options={finalOptions}
      optionLabel={(opt) => `${opt?.id} ${opt?.name} ${opt?.type}`}
      renderOption={(opt, inToggle) => {
        if (opt?.id === '' || (inToggle && typeof opt === 'string')) {
          return (
            <div className='flex-c'>
              <Table16 />
              <Trans>Table Order</Trans>
            </div>
          )
        }
        if (opt?.id === '#random#') {
          return (
            <div className='flex-c'>
              <RandomAlt16 />
              <Trans>Random</Trans>
            </div>
          )
        }

        return (
          <RenderColumn
            withStyle={false}
            col={opt as ColumnAdapter}
            className={clsx((opt as ColumnAdapter)?.id === '' && 'text-light')}
          />
        )
      }}
    />
  )
}

export default SortBySelectFieldSetting
